import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
import { Guid } from "guid-typescript";
import ClientDetailsMixin from "../ClientDetailsMixin";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import ClientModule from "@/store/modules/client.module";
import { AuthModule } from "@/store/modules/auth.module";
import { sameId } from "@/router/views/clientUpdates/updatesHelpers";
import { ClientRiskModel } from "@/store/models/client/clientRisk";
import DeleteComponent from "@/components/deleteComponent.vue";
import Loader from "@/components/widgets/loader.vue";
import { toaster } from "@/utilities/toaster";
import { RECHECK_MODE } from "@/types/validators";
const RISK_DATE_BEFORE_START = "Risk Date should be the same as or later than the client's Start Date.";
setInteractionMode("passive");
/**
 * Client Details — Risks tab. Risk timeline with its Add/Edit modals and delete
 * confirmation (driven by the riskInfo region).
 */
let ClientRisksTabComponent = /** @class */ (() => {
    var _a;
    let ClientRisksTabComponent = class ClientRisksTabComponent extends Mixins(ClientDetailsMixin, StrictDateMixin) {
        constructor() {
            super(...arguments);
            this.recheckMode = RECHECK_MODE;
            this.clientRisk = new ClientRiskModel();
            this.editRiskDTO = new ClientRiskModel();
            this.showDeleteRiskModal = false;
            // Risks whose author photo failed to load - they fall back to initials
            this.brokenAuthorImageIds = [];
        }
        showAuthorImage(risk) {
            return !!risk.createdByImageURL && this.brokenAuthorImageIds.indexOf(String(risk.id)) === -1;
        }
        onAuthorImageError(risk) {
            this.brokenAuthorImageIds = [...this.brokenAuthorImageIds, String(risk.id)];
        }
        // Each modal is open only while the machine is in its state.
        onStateChange(state) {
            this.toggleModal("add-risk", state.matches("loadedClientContactsInfo.riskInfo.addRiskInfo"));
            this.toggleModal("edit-risk", state.matches("loadedClientContactsInfo.riskInfo.updateRiskInfo"));
        }
        toggleModal(id, visible) {
            if (visible) {
                this.$bvModal.show(id);
            }
            else {
                this.$bvModal.hide(id);
            }
        }
        addRisk() {
            const risk = new ClientRiskModel();
            // Date of Risk defaults to today (left empty if the client only starts later)
            const today = this.toCalendarDay(new Date());
            risk.riskDate = this.disableBeforeClientStart(today) ? null : today;
            this.clientRisk = risk;
            this.clearDateErrors();
            this.machineService.send({ type: "ADD_RISK" });
            // Created by defaults to the logged-in user once the employee list is in - only if they are on it
            // (the API rejects an employee without PMO Clients rights) and nobody picked one meanwhile
            ClientModule.GetClientAssignedEmployees(this.clientId).then(() => {
                const self = this.clientAssignedEmployees.find((x) => sameId(x.id, AuthModule.userIdValue));
                if (self && this.clientRisk === risk && !risk.createdBy) {
                    risk.createdBy = self.id.toString();
                }
            });
        }
        // Risk dates: nothing before the client's Start Date
        disableBeforeClientStart(date) {
            return this.isBeforeClientStartDate(date, this.client ? this.client.startDate : null);
        }
        saveClientRisk() {
            this.validate("newRiskObserver").then((res) => {
                if (res && !this.hasDateErrors) {
                    if (this.disableBeforeClientStart(this.clientRisk.riskDate)) {
                        toaster.ShowError(RISK_DATE_BEFORE_START);
                        return;
                    }
                    this.clientRisk.clientId = this.clientId.toString();
                    this.clientRisk.riskDateAsString = this.formatClientDate(this.clientRisk.riskDate);
                    this.machineService.send({
                        type: "ADDING_RISK",
                        payLoad: this.clientRisk,
                    });
                }
            });
        }
        editRisk(risk) {
            ClientModule.GetClientAssignedEmployees(this.clientId);
            // The listing returns riskDate as an ISO string; the date-picker only renders Date values.
            this.editRiskDTO = Object.assign({}, risk, { riskDate: risk.riskDate ? new Date(risk.riskDate) : null });
            this.clearDateErrors();
            this.machineService.send({ type: "UPDATE_RISK" });
        }
        updateClientRisk() {
            this.validate("editRiskObserver").then((res) => {
                if (res && !this.hasDateErrors) {
                    // Also catches an older risk whose date predates a Start Date set later
                    if (this.disableBeforeClientStart(this.editRiskDTO.riskDate)) {
                        toaster.ShowError(RISK_DATE_BEFORE_START);
                        return;
                    }
                    this.editRiskDTO.riskDateAsString = this.formatClientDate(this.editRiskDTO.riskDate);
                    this.machineService.send({
                        type: "UPDATING_RISK",
                        payLoad: this.editRiskDTO,
                    });
                }
            });
        }
        cancelRisk() {
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        // Picking a risk level (Add or Edit) always resets Goal to that level's default; the user can then change it
        onRiskLevelChange(risk) {
            const level = this.riskLevels.find((x) => x.id === risk.riskColor);
            risk.goal = level ? level.defaultGoal : null;
        }
        openDeleteRiskModal(riskId) {
            this.delRiskValueId = riskId;
            this.showDeleteRiskModal = true;
            this.machineService.send({ type: "DELETE_RISK" });
        }
        deleteRisk() {
            this.machineService.send({
                type: "DELETING_RISK",
                payLoad: this.delRiskValueId,
            });
            this.showDeleteRiskModal = false;
        }
        cancelDelete() {
            this.showDeleteRiskModal = false;
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        // Computed
        get clientRisks() {
            return ClientModule.clientRisks;
        }
        get clientAssignedEmployees() {
            return ClientModule.clientAssignedEmployees;
        }
        get riskLevels() {
            return ClientModule.riskLevels;
        }
        created() {
            ClientModule.GetRiskLevels();
        }
        get isRiskLoading() {
            return ["gettingClientRisksInfo", "addingRiskInfo", "updatingRiskInfo", "deletingRiskInfo"].some((s) => this.state.matches(`loadedClientContactsInfo.riskInfo.${s}`));
        }
    };
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientRisksTabComponent.prototype, "client", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", typeof (_a = typeof Guid !== "undefined" && Guid) === "function" ? _a : Object)
    ], ClientRisksTabComponent.prototype, "clientId", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientRisksTabComponent.prototype, "state", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientRisksTabComponent.prototype, "machineService", void 0);
    __decorate([
        Watch("state"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], ClientRisksTabComponent.prototype, "onStateChange", null);
    ClientRisksTabComponent = __decorate([
        Component({
            name: "ClientRisksTabComponent",
            components: {
                Loader,
                DeleteComponent,
            },
        })
    ], ClientRisksTabComponent);
    return ClientRisksTabComponent;
})();
export default ClientRisksTabComponent;
